import {
  ArrowRightOutlined,
  CloseOutlined,
  CopyOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { AccordionItem, Button, Heading, Input, Status } from '@mm/components';
import { useViewActions } from '@mm/state/actions/viewActions';
import classNames from 'classnames';
import { isEqual, uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import { ActivationAction } from '../../../../features/beta/modules/Dashboard/ActivationBlock/ActivationBlock';
import { useActivationSteps } from '../../../../features/beta/modules/Dashboard/ActivationBlock/use-activation-steps';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import qualityTestExplainer from './quality-test-explainer.png';
import { ReactComponent as DataQualityTestIcon } from 'assets/data-quality-test-icon.svg';
import styles from './DataQualityTestSlideout.module.css';
import { useDataQualityTestCRUDActions } from '@mm/state/actions/DataQualityTestCRUDActions';
import { useSelector } from 'react-redux';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import clappingIcon from 'assets/clapping.png';
import { HelpCenter } from '../../../../../common/components/HelpCenter/HelpCenter';
import { Checkbox, notification } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { SuperpixelSection } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/XRayIngegrations/SuperpixelSection/SuperpixelSection';

export type DataQualityTestSlideoutProps = {};

export const DataQualityTestSlideout = (
  props: DataQualityTestSlideoutProps,
) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const configuration = useSelector(
    (state: StoreState) => state.dataQualityTest.configuration,
  );
  const hasConfiguration = !!configuration?.authKeys;
  const viewActions = useViewActions();
  const { setRef } = useSlideoutOnClose();
  const { actions } = useActivationSteps([
    'klaviyo',
    'salesforce',
    'sendlane',
    'shopify',
    'hubspot',
    'omnisend',
  ]);
  const { createConfiguration, updateConfiguration } =
    useDataQualityTestCRUDActions();
  const { openNewInfoModal, closeModal } = useModalActions();
  const closeSlideout = async () => {
    viewActions.hideSlideoutMenu();
  };

  const activeActions = actions
    .filter(
      action =>
        !action.hide && ['xray', 'integrations'].includes(action.keyName),
    )
    .map(action => ({
      ...action,
      onClick: () => {
        action.onClick?.();
        closeSlideout();
      },
    }));
  const isCompleted = activeActions.every(action => action.completed);
  const [step, setStep] = useState<number>(1);
  const [selectedWebhookType, setSelectedWebhookType] = useState<string[]>(
    configuration.dataSources || ['retention'],
  );
  const activeConfiguration = configuration?.state === 'active';
  const status = activeConfiguration ? 'Running...' : 'Not Running';
  const statusColor = activeConfiguration ? 'blue' : '#BFBFBF';
  const normalizedDomain = () => {
    const domain = configuration?.domain;

    if (!domain) {
      return 'https://api.mobilemonkey.com/api/data_quality_test/contacts';
    }

    const trimmed = domain.trim();

    if (trimmed.startsWith('http')) {
      return trimmed;
    }

    return `https://${trimmed}`;
  };

  const HELP_LINK =
    'https://customers.ai/help/l/en/article/4c3pf1a9gj-data-quality-test';
  const webhookUrl = normalizedDomain();

  const CUSTOM_HEADERS = `{
  "Authorization": "${configuration.authKeys?.retention}"
}`;

  const onWebhookSelect = (type: 'opensend' | 'retention') => {
    const webhookExists = selectedWebhookType.find(v => v === type);
    if (webhookExists) {
      setSelectedWebhookType(values => values.filter(v => v !== type));
    } else {
      setSelectedWebhookType(values => [...values, type]);
    }

    setStep((prevStep: number) => prevStep + 1);
  };

  const createConfig = async () => {
    await createConfiguration({
      facebookPageId,
      state: 'terminated',
      dataSources: selectedWebhookType ?? [],
    });
  };

  const closeAll = () => {
    closeModal();
    closeSlideout();
  };

  const renderStepHeader = (
    title: string,
    notificationMsg: string,
    copyText: string,
  ) => (
    <div className={styles.stepHeader}>
      {' '}
      {title}
      <CopyToClipboard
        text={copyText}
        onCopy={() =>
          notification.success({
            message: notificationMsg,
          })
        }
      >
        <Button
          leftIcon={<CopyOutlined />}
          css={{ container: styles.button }}
          color="secondary-blue"
        >
          Copy
        </Button>
      </CopyToClipboard>
    </div>
  );

  const hasChanges = !isEqual(configuration?.dataSources, selectedWebhookType);

  const onTestStart = async () => {
    await updateConfiguration({
      facebookPageId,
      state: 'active',
      dataSources: selectedWebhookType ?? [],
    });
    if (!activeConfiguration) {
      openNewInfoModal({
        image: clappingIcon,
        title: (
          <div className={styles.modalContainer}>
            <div className={styles.tag}>You did a great job!</div>
            Your results will be ready when we gather some data
          </div>
        ),
        button: 'Got It',
        css: {
          container: styles.modalContainer,
          button: styles.secondaryBlueBtn,
        },
        onClick: closeAll,
        onClose: closeAll,
      });
    }
  };

  useEffect(() => {
    if (isCompleted && selectedWebhookType.length) {
      if (!hasConfiguration) {
        createConfig();
      }
      setStep(3);
    }
  }, [isCompleted]);

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        <Heading className={styles.headerTitle} size={3}>
          Data Quality Test
        </Heading>

        <div className={styles.headerButtons}>
          <Status
            color={statusColor}
            status={status}
            className={styles.status}
          />
          <Button
            css={{ container: styles.button }}
            onClick={onTestStart}
            disabled={step < 2 || !hasChanges || !selectedWebhookType.length}
          >
            Save
          </Button>
          <CloseOutlined onClick={closeSlideout} className={styles.closeIcon} />
        </div>
      </div>
      <SlideoutMenu.Content className={styles.content}>
        <AccordionItem
          openOnMount={!hasConfiguration}
          closeOnOutsideClick={false}
          className={styles.accordion}
          titleComponent={
            <div className={styles.accordionTitle}>
              <DataQualityTestIcon />
              How does quality test “Customers.ai Vs. Competitors” work?
            </div>
          }
        >
          <img
            src={qualityTestExplainer}
            alt="Quality Test"
            className={styles.explainer}
          />
        </AccordionItem>
        <div className={styles.stepsContainer}>
          <div className={styles.box}>
            <div className={styles.stepTitle}>
              1. Complete Customers.ai account activation
            </div>
            <div className={styles.steps}>
              {activeActions.map(action => (
                <ActivationAction key={uniqueId()} {...action} />
              ))}
            </div>
          </div>
          <div className={styles.box}>
            <SuperpixelSection
              className={styles.superPixel}
              onBeforeTitle={<span>2.</span>}
              caretStyleIcon={false}
              styles={{ instruction: styles.instruction }}
            />
          </div>
          <div
            className={classNames(styles.box, {
              [styles.disabled]: step < 2,
            })}
          >
            <div className={styles.stepTitle}>3. Set up your webhooks</div>
            <div className={styles.steps}>
              <div>a. What Pixel are you testing?</div>

              <Checkbox
                value="retention"
                checked={selectedWebhookType.includes('retention')}
                onChange={e => onWebhookSelect(e.target.value)}
                className={styles.checkbox}
              >
                {' '}
                Retention
              </Checkbox>
              <Checkbox
                value="opensend"
                checked={selectedWebhookType.includes('opensend')}
                onChange={e => onWebhookSelect(e.target.value)}
                className={styles.checkbox}
              >
                {' '}
                Opensend
              </Checkbox>
            </div>
            <div
              className={classNames(styles.steps, {
                [styles.disabled]: !selectedWebhookType,
              })}
            >
              <div>b. To install webhooks follow the instruction</div>
              <Button
                css={{
                  container: styles.secondaryBlueBtn,
                }}
                color="secondary-blue"
                onClick={() => window.open(HELP_LINK)}
              >
                Go to Install Instruction
              </Button>
            </div>

            {selectedWebhookType.includes('retention') && (
              <AccordionItem
                className={styles.stepAccordion}
                title="Install Instructions for Retention"
                closeOnOutsideClick={false}
                caretStyleIcon
                openOnMount
              >
                <div
                  className={classNames(styles.steps, {
                    [styles.disabled]: !selectedWebhookType,
                  })}
                >
                  {renderStepHeader(
                    'Copy the Webhook URL',
                    'Webhook URL is copied!',
                    webhookUrl,
                  )}

                  <Input
                    type="text"
                    readOnly
                    value={webhookUrl}
                    css={{ container: styles.input }}
                  />
                </div>
                <div
                  className={classNames(styles.steps, {
                    [styles.disabled]: !selectedWebhookType,
                  })}
                >
                  {renderStepHeader(
                    'Custom Headers',
                    'Headers are copied!',
                    CUSTOM_HEADERS,
                  )}
                  <TextArea readOnly rows={3} value={CUSTOM_HEADERS} />
                </div>
              </AccordionItem>
            )}
            {selectedWebhookType.includes('opensend') && (
              <AccordionItem
                className={styles.stepAccordion}
                title="Install Instructions for Opensend"
                closeOnOutsideClick={false}
                openOnMount
                caretStyleIcon
              >
                <div
                  className={classNames(styles.steps, {
                    [styles.disabled]: !selectedWebhookType,
                  })}
                >
                  {renderStepHeader(
                    'Copy the Webhook URL',
                    'Webhook URL is copied!',
                    webhookUrl,
                  )}

                  <Input
                    type="text"
                    readOnly
                    value={webhookUrl}
                    css={{ container: styles.input }}
                  />
                </div>

                <div
                  className={classNames(styles.steps, {
                    [styles.disabled]: !selectedWebhookType,
                  })}
                >
                  {renderStepHeader(
                    ' Copy the API Key for webhooks authentication',
                    'API Key is copied!',
                    configuration?.authKeys?.opensend || '',
                  )}
                  <Input
                    type="text"
                    readOnly
                    value={configuration?.authKeys?.opensend}
                    css={{ container: styles.input }}
                  />
                </div>
              </AccordionItem>
            )}
          </div>
          {!activeConfiguration && (
            <div className={classNames(styles.box)}>
              <div
                className={classNames(styles.stepTitle, {
                  [styles.disabled]: step < 3,
                })}
              >
                3. Start competition
              </div>
              <Button
                css={{
                  container: classNames(styles.primaryButton),
                }}
                color="blue"
                onClick={onTestStart}
                disabled={step < 3}
              >
                <ArrowRightOutlined />
                Begin Test
              </Button>
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <div>Having trouble?</div>
          <HelpCenter
            helpLink={HELP_LINK}
            title="Go to Help Center"
            className={styles.helpCenter}
            icon={<FileOutlined className={styles.icon} />}
          />
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
